
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { Student } from "@/services/db/types";
import { format } from "date-fns";
import { toast } from "sonner";

/**
 * Export detailed student profile as JPG - ULTRA-FAST
 */
export const printStudentProfileAsJPG = async (student: Student) => {
  try {
    const loadingToast = toast.loading('Generating student profile JPG...');

    const { content, filename, styles } = generateStudentProfileContent(student);
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as JPG with ultra-fast settings
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student profile');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student profile downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading student profile:', error);
    toast.error('Failed to download student profile');
  }
};

/**
 * Export detailed student profile as PDF - ULTRA-FAST
 */
export const printStudentProfileAsPDF = async (student: Student) => {
  try {
    const loadingToast = toast.loading('Generating student profile PDF...');

    const { content, filename, styles } = generateStudentProfileContent(student);
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as PDF with ultra-fast settings
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student profile');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student profile downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading student profile:', error);
    toast.error('Failed to download student profile');
  }
};

/**
 * Legacy function - now calls JPG version for backward compatibility
 */
export const printStudentProfile = async (student: Student) => {
  await printStudentProfileAsJPG(student);
};

/**
 * Generate common content for both JPG and PDF downloads
 */
const generateStudentProfileContent = (student: Student) => {
  // Get full student name including surname
  const fullName = student.surname ? `${student.name} ${student.surname}` : student.name;

  const content = `
    <div class="print-page">
      ${getHeaderHtml('Student Profile')}
      
      <div class="print-content">
        <div class="profile-container">
          <div class="avatar-section">
            ${
              student.profileImage
                ? `<img src="${student.profileImage}" class="student-avatar profile-image" alt="${fullName} profile" style="display: block !important; visibility: visible !important; opacity: 1 !important;" />`
                : ''
            }
            <div class="img-fallback" style="${student.profileImage ? 'display:none;' : ''}">
              ${fullName && fullName.length > 0 ? fullName.charAt(0).toUpperCase() : '?'}
            </div>
          </div>
          <div class="main-info">
            <div>
              <div class="student-name">${fullName}</div>
              <div class="student-id">Student ID: ${student.id}</div>
            </div>
            <div class="section">
              <div class="section-title">Personal Information</div>
              <div class="info-grid">
                <div class="info-item"><span class="info-label">Father's Name:</span> ${student.fathersName || 'N/A'}</div>
                <div class="info-item"><span class="info-label">Age:</span> ${student.age || 'N/A'}</div>
                <div class="info-item"><span class="info-label">Address:</span> ${student.address || 'N/A'}</div>
                <div class="info-item"><span class="info-label">Country:</span> ${student.country || 'N/A'}</div>
              </div>
            </div>
            <div class="section">
              <div class="section-title">Contact Information</div>
              <div class="info-grid">
                <div class="info-item"><span class="info-label">Email:</span> ${student.email || 'N/A'}</div>
                <div class="info-item"><span class="info-label">Contact Number:</span> ${student.contactNumber || 'N/A'}</div>
              </div>
            </div>
            <div class="section">
              <div class="section-title">Enrollment Information</div>
              <div class="info-grid">
                <div class="info-item"><span class="info-label">Enrollment Date:</span> ${student.createdAt ? format(new Date(student.createdAt), 'PPP') : 'N/A'}</div>
                <div class="info-item"><span class="info-label">Programs:</span> ${student.programs ? student.programs.length : 0}</div>
              </div>
            </div>
          </div>
        </div>
        
        ${getSignatureHtml('student')}
      </div>
      
      ${getFooterHtml('student')}
    </div>
  `;

  const styles = `
    ${getCommonPrintStyles()}
    .profile-container {
      margin-top: 20px;
      display: flex;
      gap: 32px;
      align-items: flex-start;
    }
    .avatar-section {
      min-width: 120px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .student-avatar {
      width: 100px !important;
      height: 100px !important;
      border-radius: 50% !important;
      border: 4px solid #f5f5f5 !important;
      object-fit: cover !important;
      background: #E5DEFF !important;
      margin-bottom: 12px !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      image-rendering: -webkit-optimize-contrast !important;
      image-rendering: crisp-edges !important;
      -ms-interpolation-mode: bicubic !important;
    }
    .profile-image {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      image-rendering: -webkit-optimize-contrast !important;
      image-rendering: crisp-edges !important;
      -ms-interpolation-mode: bicubic !important;
    }
    .img-fallback {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      font-size: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      background: #8B5CF6;
      font-weight: bold;
      margin-bottom: 12px;
      border: 4px solid #f5f5f5;
    }
    .main-info {
      flex: 1;
      background: #F1F0FB;
      border-radius: 12px;
      padding: 24px 20px 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      box-shadow: 0 2px 8px #0001;
    }
    .section {
      margin-bottom: 6px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
    }
    .section:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
    .section-title {
      font-size: 1.1em;
      font-weight: 600;
      color: #6E59A5;
      margin-bottom: 6px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 7px 30px;
    }
    .info-item {
      font-size: 0.97em;
      margin-bottom: 3px;
      color: #444;
    }
    .info-label {
      font-weight: 500;
      color: #8E9196;
      margin-right: 4px;
    }
    .student-name {
      font-size: 1.5em;
      font-weight: bold;
      margin-bottom: 2px;
      color: #3D7069;
      letter-spacing: 0.02em;
    }
    .student-id {
      font-size: 0.95em;
      color: #888;
      margin-bottom: 9px;
      font-weight: 500;
    }
    @media print {
      .main-info { box-shadow: none; }
      .profile-container { gap: 20px; }
      .student-avatar, .img-fallback { width: 80px; height: 80px; }
      .student-avatar {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        image-rendering: -webkit-optimize-contrast !important;
        image-rendering: crisp-edges !important;
      }
      .profile-image {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        image-rendering: -webkit-optimize-contrast !important;
        image-rendering: crisp-edges !important;
      }
    }
  `;

  // Create filename
  const filename = `Student_Profile_${fullName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;

  return { 
    content, 
    filename, 
    styles 
  };
};
